// Waveform — thin WaveSurfer.js wrapper used on Take-Lanes (and, later,
// Master-Clip inspectors). Owns its own WS instance; the parent gets an
// imperative handle with play/pause/seekTo and event callbacks for play-
// state + drag-select regions.
//
// Audio playback IS NOT driven by WaveSurfer's internal engine here.
// Background: WaveSurfer's WebAudioPlayer pauses + restarts its underlying
// AudioBufferSourceNode on every `setTime()` call (the seek path in
// WebAudioPlayer.set currentTime is `_pause()` then `_play()`). When we drove
// the visual playhead by polling currentTime via `setTime` from a rAF loop,
// every frame caused a tiny pause/restart of the audio source — the source
// of the audible + visual stutter on Take cards (FB#TBD).
//
// Fix: keep WaveSurfer for waveform RENDERING and click-to-seek INTERACTION,
// but drop its audio engine entirely. Audio is played via the unified
// `audioPlayback.playAudio()` helper (same path TTS take chips and version
// pills already use, on the SHARED AudioContext). The waveform's visual
// cursor is moved via `wsRef.current.setTime(t)` from a rAF loop reading
// `playHandle.getCurrentTime()` — and `setTime` only mutates a non-audio
// stand-in `media` object so it has zero side-effects on the actual playback.
//
// The stand-in is a `DisplayMedia` instance (defined below) — an EventTarget
// with the surface area Player.ts (the WaveSurfer base class) needs from a
// `media` element: paused/seeking/duration/currentTime/volume/muted/play()/
// pause()/setSrc-via-`src=`/etc. None of it touches Web Audio.

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import {
  decodeAndCache,
  playAudio,
  type PlayHandle,
} from '@/lib/audioPlayback';
import { getAudioContext } from '@/lib/audioContext';
import styles from './Waveform.module.css';

export interface WaveformHandle {
  play: () => void;
  pause: () => void;
  seekTo: (seconds: number) => void;
  isPlaying: () => boolean;
}

interface Props {
  audio: ArrayBuffer | Blob;
  height: number;
  color?: string;
  progressColor?: string;
  /** Fires when the user drag-selects a horizontal region on the waveform.
   *  The region is consumed immediately (removed from the WS view) so only
   *  one selection exists at a time — the MasterClip on the master timeline
   *  is the persistent representation of the selection. */
  onRegion?: (start: number, end: number) => void;
  onPlay?: () => void;
  onFinish?: () => void;
  onPause?: () => void;
  /** Fires once the underlying audio buffer is decoded + WS has loaded
   *  the blob — i.e., a subsequent `.play()` on the handle will actually
   *  produce audio rather than silently no-op against a null buffer. */
  onReady?: () => void;
  cursorPosition?: number;
}

// ── DisplayMedia ───────────────────────────────────────────────────────────
// Minimal HTMLMediaElement-shaped EventTarget with no audio side effects.
// WaveSurfer's Player base class duck-types this contract; everything we
// implement here is what Player reads/writes during normal use, plus enough
// extras (canPlayType, removeAttribute) to satisfy setSrc().
class DisplayMedia extends EventTarget {
  // ── Player-read state ─────────────────────────────────────────────────
  paused = true;
  ended = false;
  seeking = false;
  duration = 0;
  volume = 1;
  muted = false;
  playbackRate = 1;
  controls = false;
  autoplay = false;
  preservesPitch = true;
  crossOrigin: string | null = null;

  // src / currentSrc — Player.getSrc() reads these to revoke blob URLs on
  // destroy. We accept whatever URL WaveSurfer assigns without touching it
  // (no fetch, no decode — WaveSurfer's loadAudio path handles decoding via
  // its own fetcher when given the blob through loadBlob).
  private _src = '';
  private _currentTime = 0;

  get src(): string { return this._src; }
  set src(value: string) {
    this._src = value;
    if (value) {
      // Mirror HTMLAudioElement's load order so WaveSurfer's setupReactive
      // listeners fire as expected. We don't actually decode anything — the
      // Waveform component pre-decodes via decodeAndCache and feeds duration
      // directly into ws.loadBlob(_, _, duration), skipping the loadedmetadata
      // wait entirely; these events are only here for parity with the
      // contract Player.setupReactiveMediaEvents subscribes to.
      this.dispatchEvent(new Event('loadedmetadata'));
      this.dispatchEvent(new Event('canplay'));
    } else {
      this.dispatchEvent(new Event('emptied'));
    }
  }
  get currentSrc(): string { return this._src; }

  get currentTime(): number { return this._currentTime; }
  set currentTime(value: number) {
    this._currentTime = Math.max(0, Math.min(value, this.duration || value));
    // WS Player.setupReactiveMediaEvents expects 'seeking' on every seek.
    // We emit 'seeked' immediately after — there's no actual seek operation
    // to wait on, this is a display element.
    this.seeking = true;
    this.dispatchEvent(new Event('seeking'));
    this.dispatchEvent(new Event('timeupdate'));
    this.seeking = false;
    this.dispatchEvent(new Event('seeked'));
  }

  // play()/pause() are called by Player.play()/pause(). We don't drive audio
  // from here — the Waveform component intercepts its imperative `play()`
  // before it ever reaches WS. These exist so any stray internal call (e.g.
  // a future WS feature) doesn't NPE on `media.play is not a function`.
  async play(): Promise<void> {
    this.paused = false;
    this.ended = false;
    this.dispatchEvent(new Event('play'));
  }
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.dispatchEvent(new Event('pause'));
  }

  // load() — called by WS's destroy(); resets the element. We're an external
  // media so destroy() returns early before reaching this, but keep it for
  // safety.
  load(): void { /* no-op */ }

  // canPlayType — WS's setSrc uses this to decide whether to wrap a Blob in
  // an object URL. Returning a non-empty string for audio/* matches the
  // behaviour of HTMLAudioElement on most browsers. Note: Player.canPlayType
  // checks for non-empty strings to determine playability.
  canPlayType(type: string): string {
    return /^(audio|video)\//.test(type) ? 'maybe' : '';
  }

  removeAttribute(attr: string): void {
    if (attr === 'src') {
      this._src = '';
      this.dispatchEvent(new Event('emptied'));
    }
  }

  // setSinkId — never called in our code path but Player.setSinkId forwards
  // to this. Resolve to an empty promise so plugins that introspect don't
  // throw; sink switching isn't a feature we expose.
  async setSinkId(_sinkId: string): Promise<void> { /* no-op */ }
}

export const Waveform = forwardRef<WaveformHandle, Props>(function Waveform(
  {
    audio,
    height,
    color = 'var(--muted, #c8c8c8)',
    progressColor = 'var(--brandcolor, #31686C)',
    onRegion,
    onPlay,
    onFinish,
    onPause,
    onReady,
    cursorPosition,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const mediaRef = useRef<DisplayMedia | null>(null);
  // Active audioPlayback handle for THIS waveform — null when not playing.
  const playRef = useRef<PlayHandle | null>(null);
  // rAF id for the visual-cursor sync loop.
  const rafRef = useRef<number | null>(null);
  // Decoded buffer (rendered by WS too, but we keep our own ref so seek
  // and pause-resume can re-issue playAudio with the same source without
  // hitting the cache key path twice). Filled in the load effect.
  const bufferRef = useRef<AudioBuffer | null>(null);
  // Stable cache key per mounted instance — keeps the audioPlayback cache
  // tight (one entry per take, not one per click). Re-decode-on-invalidate
  // is handled by audioPlayback's onPreviewAudioInvalidate subscription.
  const cacheKeyRef = useRef<string>('');
  // (Mixer override pattern removed — now handled by audioPlayback's
  // bypassMixer option, which compensates the per-play gain for the live
  // mixer fader value without mutating store state. Cleaner: no visual
  // fader jolt for users with the mixer popover open during preview.)

  // Callback props mirrored into refs so the WS-init effect doesn't depend
  // on callback identity — inline arrow functions from parents would
  // otherwise rebuild the WaveSurfer instance on every render.
  const onPlayRef = useRef(onPlay);
  const onFinishRef = useRef(onFinish);
  const onPauseRef = useRef(onPause);
  const onRegionRef = useRef(onRegion);
  const onReadyRef = useRef(onReady);
  onPlayRef.current = onPlay;
  onFinishRef.current = onFinish;
  onPauseRef.current = onPause;
  onRegionRef.current = onRegion;
  onReadyRef.current = onReady;
  // Only the PRESENCE of onRegion shapes the WaveSurfer instance
  // (dragToSeek); the callback itself is read through onRegionRef.
  const hasRegion = !!onRegion;

  // ── Helpers ────────────────────────────────────────────────────────────

  const stopRaf = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const startRaf = () => {
    if (rafRef.current != null) return;
    const tick = () => {
      const handle = playRef.current;
      const ws = wsRef.current;
      if (!handle || !ws) {
        rafRef.current = null;
        return;
      }
      // Drive WS's visual cursor by mutating the DisplayMedia's currentTime
      // — this fires 'timeupdate' on the WS Player which moves the cursor.
      // Zero audio side-effect because DisplayMedia doesn't touch Web Audio.
      try {
        ws.setTime(handle.getCurrentTime());
      } catch { /* WS destroyed mid-frame */ }
      if (handle.isPlaying()) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const playInternal = async (fromSec?: number) => {
    const buf = bufferRef.current;
    if (!buf) return;
    // Stop any existing playback before starting new — playAudio doesn't
    // implicitly stop the previous handle since we manage the lifetime.
    playRef.current?.stop();
    playRef.current = null;

    const handle = await playAudio({
      source: buf,
      cacheKey: cacheKeyRef.current,
      startFromSec: fromSec,
      // Route through voice-bus so the Safari MediaStream tap (set up in
      // audioContext.ts on Safari) keeps audio audible across context
      // suspend/resume cycles. Direct routing into ac.destination drops
      // ~0.3s every time Safari briefly suspends mid-playback.
      routing: 'voice-bus',
      // bypassMixer = unity playback regardless of Voice fader. Take
      // previews are an inspection tool — should always be audible at
      // reference level even if the talent has the mixer pulled down.
      bypassMixer: true,
      onEnded: () => {
        // Snap the visual cursor to the end so the UI shows "finished".
        try { wsRef.current?.setTime(buf.duration); } catch { /* ignore */ }
        playRef.current = null;
        stopRaf();
        // Sync DisplayMedia state so Player.isPlaying() reads false after.
        const m = mediaRef.current;
        if (m) {
          m.paused = true;
          m.ended = true;
        }
        onFinishRef.current?.();
      },
    });
    playRef.current = handle;

    // Sync DisplayMedia + emit 'play' so parents wired to onPlay see the
    // transition. WS Player listens to 'play' on its media element to flip
    // its isPlayingSignal — keeping the dummy in sync means
    // wsRef.current?.isPlaying() returns truthful values too.
    const m = mediaRef.current;
    if (m) {
      m.paused = false;
      m.ended = false;
      m.dispatchEvent(new Event('play'));
    }
    onPlayRef.current?.();
    startRaf();
  };

  const pauseInternal = () => {
    const h = playRef.current;
    if (!h) return;
    h.pause();
    stopRaf();
    const m = mediaRef.current;
    if (m) {
      m.paused = true;
      m.dispatchEvent(new Event('pause'));
    }
    onPauseRef.current?.();
  };

  // ── Imperative handle ──────────────────────────────────────────────────

  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        const h = playRef.current;
        // Resume from paused position if we have a paused handle, else
        // start fresh from current cursor.
        if (h && !h.isPlaying()) {
          h.resume();
          // Re-sync DisplayMedia + restart rAF (audioPlayback doesn't
          // notify us back; we observe via isPlaying).
          const m = mediaRef.current;
          if (m) {
            m.paused = false;
            m.dispatchEvent(new Event('play'));
          }
          onPlayRef.current?.();
          startRaf();
          return;
        }
        const ws = wsRef.current;
        const fromSec = ws ? ws.getCurrentTime() : 0;
        void playInternal(fromSec);
      },
      pause: () => {
        pauseInternal();
      },
      seekTo: (seconds: number) => {
        const ws = wsRef.current;
        if (!ws) return;
        const dur = ws.getDuration();
        if (!dur) return;
        const clamped = Math.max(0, Math.min(seconds, dur));
        ws.setTime(clamped);
        const h = playRef.current;
        if (h) h.seek(clamped);
      },
      isPlaying: () => playRef.current?.isPlaying() ?? false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- playInternal/pauseInternal lesen nur Refs; die Closure des ersten Renders ist gleichwertig, der Handle soll stabil bleiben
    [],
  );

  // ── Init effect (runs on audio change) ─────────────────────────────────

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Generate a fresh cache key per mount so unmount→remount with the same
    // buffer doesn't collide across instances. Audio identity is per take —
    // re-mounting the same take card re-decodes; that's fine, the cache
    // sits behind the key and a same-take repeat play within the lifetime
    // of one mount hits the cache.
    cacheKeyRef.current = `wf-${Math.random().toString(36).slice(2)}`;

    // Pre-decode the audio so we can pass duration to ws.loadBlob (skipping
    // its loadedmetadata wait), and so playInternal can hand the AudioBuffer
    // straight to playAudio.
    const blob =
      audio instanceof Blob ? audio : new Blob([audio], { type: 'audio/mpeg' });
    const arrPromise = (audio instanceof Blob ? blob.arrayBuffer() : Promise.resolve(audio));

    // WaveSurfer paints on canvas and cannot resolve CSS vars directly.
    const resolveColor = (cssVal: string): string => {
      if (!cssVal.includes('var(')) return cssVal;
      const match = cssVal.match(/var\(([^,)]+)(?:,\s*([^)]+))?\)/);
      if (!match) return cssVal;
      const varName = match[1].trim();
      const fallback = (match[2] ?? '').trim();
      const computed = getComputedStyle(el).getPropertyValue(varName).trim();
      return computed || fallback || '#31686C';
    };

    // Display-only media stand-in. WS uses this for setTime/play/pause/etc.
    // — none of which produces audible output. Real audio is driven from
    // playInternal via audioPlayback.playAudio().
    const media = new DisplayMedia();
    mediaRef.current = media;

    let cancelled = false;

    const ws = WaveSurfer.create({
      container: el,
      // No `url` — we load the blob imperatively below with a known duration
      // so WS skips its own loadedmetadata wait on the dummy media.
      // No `backend` — defaults to MediaElement, but with media supplied WS
      // doesn't construct its own player anyway.
      media: media as unknown as HTMLMediaElement,
      waveColor: resolveColor(color),
      progressColor: resolveColor(progressColor),
      cursorColor: 'rgba(0, 0, 0, 0.65)',
      cursorWidth: 2,
      height,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      normalize: true,
      interact: true,
      // dragToSeek: ONLY when this Waveform doesn't expose a region-
      // select callback. When `onRegion` is wired, drags belong to the
      // RegionsPlugin (drag a range = create a clip selection); leaving
      // dragToSeek=true would let WS's seek handler eat the drag and
      // move the playhead instead. Click-to-seek (interact: true)
      // remains in both modes.
      dragToSeek: !hasRegion,
      hideScrollbar: true,
    });
    wsRef.current = ws;

    // ── Decode + load ──────────────────────────────────────────────────
    void (async () => {
      try {
        const arr = await arrPromise;
        if (cancelled) return;
        const buf = await decodeAndCache(arr.slice(0), cacheKeyRef.current);
        if (cancelled) return;
        bufferRef.current = buf;
        media.duration = buf.duration;
        // Load the blob into WS for visual rendering. Pass the known duration
        // so WS skips its `loadedmetadata` wait. The blob fetch path that WS
        // uses to read the audio data into its own decoder fires here too.
        await ws.loadBlob(blob, undefined, buf.duration);
        if (!cancelled) onReadyRef.current?.();
      } catch (e) {
        if (!cancelled) console.warn('[waveform] load failed', e);
      }
    })();

    // ── WS event wiring ────────────────────────────────────────────────
    // `interaction` fires on user click/drag with the new time in seconds.
    // Mirror into the playHandle so the audio + paused-position both track
    // the cursor. When the handle is currently playing, seek() restarts the
    // underlying source at the new offset; when paused, seek() just stores
    // the new resume point so the next play() picks up there. Matches the
    // prior WebAudioPlayer behaviour: clicking a paused waveform moves the
    // cursor without auto-starting playback, but a subsequent play resumes
    // from the new spot rather than the old paused position.
    const onInteraction = (newTime: number) => {
      const h = playRef.current;
      if (h) h.seek(newTime);
    };
    ws.on('interaction', onInteraction);

    // Region drag-select — same plumbing as before.
    const regions = ws.registerPlugin(RegionsPlugin.create());
    const regionColor = 'rgba(49, 104, 108, 0.25)';
    let disableDragSelection: (() => void) | null = null;
    const onRegionCreated = (region: { start: number; end: number; remove: () => void }) => {
      const cb = onRegionRef.current;
      if (!cb) {
        region.remove();
        return;
      }
      const start = Math.min(region.start, region.end);
      const end = Math.max(region.start, region.end);
      if (end - start > 0.05) cb(start, end);
      region.remove();
    };
    regions.on('region-created', onRegionCreated);
    const enable = () => {
      disableDragSelection = regions.enableDragSelection({
        color: regionColor,
        drag: false,
        resize: false,
      });
    };
    if (ws.getDuration() > 0) enable();
    else ws.on('ready', enable);

    // Best-effort resume of the shared ctx — first user click on play / a
    // waveform interaction will resume it regardless, but a pre-emptive
    // nudge cuts the perceived play latency further.
    const sharedCtx = getAudioContext();
    if (sharedCtx.state === 'suspended') {
      void sharedCtx.resume().catch(() => { /* ignore */ });
    }

    return () => {
      cancelled = true;
      stopRaf();
      // Stop active playback so navigating away / unmounting doesn't leave
      // a Player running on the shared ctx.
      playRef.current?.stop();
      playRef.current = null;
      bufferRef.current = null;
      disableDragSelection?.();
      // WS treats `media` as externally owned and skips its own URL revoke
      // on destroy (`if (this.isExternalMedia) return`). Pull the blob URL
      // off our DisplayMedia and revoke it manually so loadBlob's auto-
      // generated object URL doesn't leak across remounts.
      const leakedSrc = media.src;
      try {
        ws.destroy();
      } catch {
        /* destroy can throw if load is still in flight */
      }
      if (leakedSrc.startsWith('blob:')) {
        try { URL.revokeObjectURL(leakedSrc); } catch { /* ignore */ }
      }
      wsRef.current = null;
      mediaRef.current = null;
    };
  }, [audio, height, color, progressColor, hasRegion]);

  // ── External cursor sync (used by callers driving the cursor from a
  // different time source). ──────────────────────────────────────────────
  useEffect(() => {
    if (cursorPosition == null) return;
    const ws = wsRef.current;
    if (!ws) return;
    const dur = ws.getDuration();
    if (!dur) return;
    const clamped = Math.max(0, Math.min(cursorPosition, dur));
    ws.setTime(clamped);
  }, [cursorPosition]);

  return <div ref={containerRef} className={styles.root} />;
});
